const NAMED_ENTITIES: Record<string, string> = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
};

/** Highest valid Unicode code point; String.fromCodePoint throws above it. */
const MAX_CODE_POINT = 0x10ffff;

function decodeEntities(text: string): string {
  return text
    .replace(/&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;|&apos;/g, (m) => NAMED_ENTITIES[m] ?? m)
    .replace(/&#(\d+);/g, (match: string, code: string) => {
      // String.fromCodePoint THROWS RangeError outside the Unicode range, and
      // this runs on the invoice-email editor's render/keystroke path via
      // htmlVisibleLength — so a stored body containing `&#999999999;` crashed
      // the editor instead of degrading. Leave anything out of range as-is,
      // exactly as the named-entity branch above leaves `&copy;` alone.
      const n = Number(code);
      return Number.isInteger(n) && n >= 0 && n <= MAX_CODE_POINT
        ? String.fromCodePoint(n)
        : match;
    });
}

/**
 * Matches a complete tag, treating a quoted attribute value as opaque so a `>`
 * inside it does not end the match early. The previous `/<[^>]+>/g` stopped at
 * the first `>`, so `<a title="a>b">x</a>` leaked `b">` into the "plain text"
 * — and htmlVisibleLength then counted that markup against the user's limit.
 */
const HTML_TAG = /<(?:[^>"']|"[^"]*"|'[^']*')*>/g;

/**
 * Renders the stored HTML email body as plain text for display/counting —
 * block-level tags and <br> become newlines so the structure the user typed is
 * preserved (textContent alone would collapse paragraphs onto one line).
 */
export function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return "";
  const withBreaks = html
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\s*li[^>]*>/gi, "• ")
    .replace(/<\/\s*(p|div|li|h[1-6]|blockquote|ul|ol|pre)\s*>/gi, "\n");
  const stripped = withBreaks.replace(HTML_TAG, "");
  return decodeEntities(stripped).replace(/\n{3,}/g, "\n\n").trim();
}

/** Visible-text length of an HTML body — the user-facing character count. */
export function htmlVisibleLength(html: string | null | undefined): number {
  return htmlToPlainText(html).length;
}
